/**
 * @fileoverview "Which plant is this photo of?" (B160): the one sheet where a photo Grow filed by itself is moved to the right
 * plant. It is MoveToPlantField's list of the garden (each plant with its bed, the plant the photo is on ticked) with a Cancel and
 * Move foot (ActionRow, CLAUDE.md rule 7). Move calls PUT /photos/:plantObjId/:photoId/plant (movePhotoToPlant), the server's one
 * path for a move (files first, then the row, rolled back together), then reads the tray and the garden again, so the strip's line
 * names the new plant and the plant cards show the photo where it now is. Cancel, the close X and a tap outside send nothing.
 *
 * MovePhotoHost draws it once, in the layout, for whichever door asked (lib/movePhoto askMovePhoto): the Photos page's strip, the
 * toast right after adding a photo (which outlives the sheet it was said from) and the plant card's line. The garden's plants are
 * read with GET /plants only while it is open.
 *
 * Where: Layout (MovePhotoHost); the doors are FiledStrip, sayFiled and PlantCardPhoto.
 * API: PUT /photos/:plantObjId/:photoId/plant (movePhotoToPlant); GET /plants while open (getPlants).
 * Words (en/es): tray.filed.moveTitle, tray.filed.move, plants.photo.api.moveFailed; sitewide.cancel (ActionRow).
 * Failures on our side (a 500, no connection): reported to Sentry and said as the standard sentence (reported, the support
 * address a link), with no "try again"; the sheet stays open.
 *
 * @module MovePhotoSheet
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { BottomPanel, PanelOption } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { FailureText } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getPlants } from '@/shared/api/plants';
import { movePhotoToPlant } from '@/shared/api/photos';
import { errorMessage } from '@/shared/lib/errorMessage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { plantWithBed } from '@/shared/lib/plantName';
import { closeMovePhoto, openMovePhoto, subscribeMovePhoto, type MovePhotoAsk } from '@/shared/lib/movePhoto';
import type { Plant } from '@/shared/types/plant';

/** The tray's cache key (features/photo-tray/lib/useTray TRAY_QUERY_KEY), read again after a move. */
const TRAY_KEY = ['tray'] as const;
/** The plants list's cache key (config/tunables/ui PLANTS_QUERY_KEY), read again after a move. */
const PLANTS_KEY = 'plants';
/** The beds' cache key (config/tunables/ui LOCATIONS_QUERY_KEY), read again after a move. */
const LOCATIONS_KEY = 'locations';

/** What the sheet is given. */
export interface MovePhotoSheetProps {
  /** The photos to move and the plant they are on now, or null when the sheet is closed. */
  ask: MovePhotoAsk | null;
  /** The garden's plants, including the one the photos are on. */
  plants: Plant[];
  /** Called when the sheet closes, moved or not. */
  onClose: () => void;
}

/**
 * The sheet itself.
 *
 * @param props - MovePhotoSheetProps.
 */
export const MovePhotoSheet: React.FC<MovePhotoSheetProps> = ({ ask, plants, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const open = ask !== null;
  /** The plant picked to move to, or null before one is. */
  const [picked, setPicked] = React.useState<number | null>(null);
  /** True while the move is on its way. */
  const [moving, setMoving] = React.useState(false);
  /** What went wrong, said under the list, or null. */
  const [failed, setFailed] = React.useState<string | null>(null);
  React.useEffect(() => { if (open) { setPicked(null); setFailed(null); } }, [open, ask]);

  /** Moves each photo to the picked plant, then reads the tray and the garden again and closes. */
  const move = async (): Promise<void> => {
    if (!ask || picked === null) return;
    setMoving(true);
    setFailed(null);
    try {
      for (const photoId of ask.photoIds) await movePhotoToPlant(ask.plantObjId, photoId, picked);
      void queryClient.invalidateQueries({ queryKey: TRAY_KEY });
      void queryClient.invalidateQueries({ queryKey: [PLANTS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [LOCATIONS_KEY] });
      onClose();
    } catch (error) {
      // On our side: reported, and the standard sentence; otherwise the refusal's own words.
      setFailed(serverFailureText(error, 'Move photo') ?? errorMessage(error, t('plants.photo.api.moveFailed')));
    } finally {
      setMoving(false);
    }
  };

  return (
    <BottomPanel open={open} onOpenChange={(next) => { if (!next && !moving) onClose(); }} title={t('tray.filed.moveTitle')} wrapTitle>
      <div role="listbox" aria-label={t('tray.filed.moveTitle')} className="mt-1 grid max-h-[50dvh] overflow-y-auto px-2">
        {plants.map((each) => {
          const shown = picked ?? ask?.plantObjId;
          return (
            <PanelOption key={each.plant_obj_id} selected={each.plant_obj_id === shown} onClick={() => setPicked(each.plant_obj_id === ask?.plantObjId ? null : each.plant_obj_id)}>
              <span className="grow truncate">{plantWithBed(each)}</span>
              {each.plant_obj_id === shown && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
            </PanelOption>
          );
        })}
      </div>
      {failed && <p role="alert" className="text-sm text-destructive"><FailureText text={failed} /></p>}
      <div className="mt-3">
        <ActionRow onCancel={onClose} disabled={moving}>
          <Button type="button" onClick={() => { void move(); }} disabled={picked === null} loading={moving}>{t('tray.filed.move')}</Button>
        </ActionRow>
      </div>
    </BottomPanel>
  );
};

/** Draws the sheet for whichever door asked (lib/movePhoto); mounted once, in the layout. */
export const MovePhotoHost: React.FC = () => {
  const { i18n: { language } } = useTranslation();
  const ask = React.useSyncExternalStore(subscribeMovePhoto, openMovePhoto, openMovePhoto);
  const { data: plants = [] } = useQuery({
    queryKey: [PLANTS_KEY, 'move-photo', language],
    queryFn: () => getPlants(undefined, language),
    enabled: ask !== null,
  });
  return <MovePhotoSheet ask={ask} plants={plants} onClose={closeMovePhoto} />;
};
